import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Server,
  Search,
  RefreshCw,
  Zap,
  Bitcoin,
  CreditCard,
  Eye,
  Building2,
  Activity,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  BarChart3,
  Key,
  Settings,
  Puzzle,
  TrendingUp,
  Plus,
  Edit,
  Copy,
  Trash2,
  Play,
  ChevronRight,
  ChevronLeft,
  FileCode,
  Globe,
  Link,
  Info,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

// ============================================
// TYPES
// ============================================

interface ProviderInstance {
  id: string;
  name: string;
  templateId: string;
  template?: {
    id: string;
    name: string;
    category: 'BAAS' | 'CRYPTO' | 'CARDS';
    type: string;
    version: string;
    credentialSchema?: CredentialField[];
  };
  tenantId: string;
  tenant?: {
    id: string;
    name: string;
  };
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR' | 'PENDING' | 'TESTING' | 'SUSPENDED' | 'DEPRECATED';
  credentials?: Record<string, string>;
  configOverrides?: Record<string, unknown>;
  webhookUrl?: string;
  priority: number;
  lastUsedAt?: string;
  lastRequestAt?: string;
  totalRequests?: number;
  requestCount?: number; // alias for totalRequests
  errorCount?: number;
  successRate?: number;
  avgResponseTime?: number;
  createdAt: string;
  updatedAt: string;
}

interface Tenant {
  id: string;
  name: string;
}

interface Account {
  id: string;
  name: string;
  type: string;
  tenantId: string;
  tenant?: { id: string; name: string };
}

interface CredentialField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'select';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
}

interface ProviderTemplate {
  id: string;
  name: string;
  category: 'BAAS' | 'CRYPTO' | 'CARDS';
  type: string;
  version: string;
  description?: string;
  status: string;
  credentialSchema?: CredentialField[];
  baseUrl?: string;
  authType?: string;
  isPublic?: boolean;
}

type WizardMode = 'INHERIT' | 'SCRATCH' | null;
type WizardStep = 1 | 2 | 3 | 4 | 5;

interface WizardFormData {
  name: string;
  accountId: string;
  templateId: string;
  category: string;
  type: string;
  baseUrl: string;
  authType: string;
  credentials: Record<string, string>;
  webhookUrl: string;
  priority: number;
}

interface ConnectionTestResult {
  status: 'PENDING' | 'TESTING' | 'SUCCESS' | 'FAILED';
  message?: string;
  latency?: number;
}

// ============================================
// CONSTANTS
// ============================================

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todas as Categorias' },
  { value: 'BAAS', label: '🏦 BaaS (Banking)' },
  { value: 'CRYPTO', label: '₿ Crypto' },
  { value: 'CARDS', label: '💳 Cartões' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os Status' },
  { value: 'ACTIVE', label: '✅ Ativo' },
  { value: 'INACTIVE', label: '⏸️ Inativo' },
  { value: 'ERROR', label: '❌ Com Erro' },
  { value: 'PENDING', label: '⏳ Pendente' },
  { value: 'TESTING', label: '🔄 Testando' },
];

const CATEGORY_INFO: Record<string, { label: string; icon: typeof Zap; gradient: string; emoji: string }> = {
  BAAS: { 
    label: 'Banking as a Service', 
    icon: Zap, 
    gradient: 'from-primary-500 to-accent-500',
    emoji: '🏦'
  },
  CRYPTO: { 
    label: 'Criptomoedas', 
    icon: Bitcoin, 
    gradient: 'from-yellow-500 to-orange-500',
    emoji: '₿'
  },
  CARDS: { 
    label: 'Cartões', 
    icon: CreditCard, 
    gradient: 'from-blue-500 to-indigo-500',
    emoji: '💳'
  },
};

const AUTH_TYPE_OPTIONS = [
  { value: 'API_KEY', label: 'API Key' },
  { value: 'BEARER', label: 'Bearer Token' },
  { value: 'BASIC', label: 'Basic Auth' },
  { value: 'OAUTH2', label: 'OAuth 2.0' },
  { value: 'CUSTOM', label: 'Customizado' },
];

const WIZARD_STEPS = [
  { step: 1, title: 'Modo', description: 'Escolha como criar' },
  { step: 2, title: 'Configuração', description: 'Template ou manual' },
  { step: 3, title: 'Credenciais', description: 'Autenticação' },
  { step: 4, title: 'Avançado', description: 'Configurações extras' },
  { step: 5, title: 'Revisão', description: 'Testar e criar' },
];

const initialWizardFormData: WizardFormData = {
  name: '',
  accountId: '',
  templateId: '',
  category: 'BAAS',
  type: 'REST',
  baseUrl: '',
  authType: 'API_KEY',
  credentials: {},
  webhookUrl: '',
  priority: 0,
};

// Helper function to safely get credentialSchema as array
const getCredentialSchemaArray = (schema: unknown): CredentialField[] => {
  if (Array.isArray(schema)) {
    return schema;
  }
  if (schema && typeof schema === 'object') {
    // If it's an object with numeric keys, convert to array
    const values = Object.values(schema);
    if (values.length > 0 && values.every(v => v && typeof v === 'object' && 'name' in v)) {
      return values as CredentialField[];
    }
  }
  return [];
};

// ============================================
// MAIN COMPONENT
// ============================================

export function SuperAdminProviders() {
  // State - List
  const [instances, setInstances] = useState<ProviderInstance[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [templates, setTemplates] = useState<ProviderTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // State - Details Modal
  const [selectedInstance, setSelectedInstance] = useState<ProviderInstance | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // State - Create Wizard
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [wizardMode, setWizardMode] = useState<WizardMode>(null);
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [wizardFormData, setWizardFormData] = useState<WizardFormData>(initialWizardFormData);
  const [wizardErrors, setWizardErrors] = useState<Record<string, string>>({});
  const [connectionTest, setConnectionTest] = useState<ConnectionTestResult>({ status: 'PENDING' });
  const [isCreating, setIsCreating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // State - Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<ProviderInstance>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // State - Clone Modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneTenantId, setCloneTenantId] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  
  // State - Delete Confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (tenantFilter) params.append('tenantId', tenantFilter);
      if (templateFilter) params.append('templateId', templateFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);

      const [instancesRes, tenantsRes, templatesRes, accountsRes] = await Promise.all([
        api.get(`/provider-instances?${params.toString()}`).catch(() => ({ data: [] })),
        api.get('/super-admin/tenants').catch(() => ({ data: { tenants: [] } })),
        api.get('/provider-templates').catch(() => ({ data: [] })),
        api.get('/accounts').catch(() => ({ data: [] })),
      ]);

      const instancesData = instancesRes.data;
      setInstances(instancesData.data || instancesData.instances || instancesData || []);
      
      const tenantsData = tenantsRes.data;
      setTenants(tenantsData.tenants || tenantsData.data || tenantsData || []);
      
      const templatesData = templatesRes.data;
      setTemplates(templatesData.data || templatesData.templates || templatesData || []);
      
      const accountsData = accountsRes.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : accountsData.data || accountsData.accounts || []);
    } catch (error) {
      console.error('Error fetching instances:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenantFilter, templateFilter, categoryFilter, statusFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debounce);
  }, [search]);

  // ============================================
  // WIZARD HANDLERS
  // ============================================

  const resetWizard = () => {
    setWizardMode(null);
    setWizardStep(1);
    setWizardFormData(initialWizardFormData);
    setWizardErrors({});
    setConnectionTest({ status: 'PENDING' });
  };

  const handleOpenCreateWizard = () => {
    resetWizard();
    setShowCreateWizard(true);
  };

  const handleCloseCreateWizard = () => {
    setShowCreateWizard(false);
    resetWizard();
  };

  const validateWizardStep = (step: WizardStep): boolean => {
    const errors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        if (!wizardMode) {
          errors.mode = 'Selecione um modo de criação';
        }
        break;
      case 2:
        if (!wizardFormData.name.trim()) {
          errors.name = 'Nome é obrigatório';
        }
        if (!wizardFormData.accountId) {
          errors.accountId = 'Selecione uma conta';
        }
        if (wizardMode === 'INHERIT' && !wizardFormData.templateId) {
          errors.templateId = 'Selecione um template';
        }
        if (wizardMode === 'SCRATCH') {
          if (!wizardFormData.baseUrl.trim()) {
            errors.baseUrl = 'URL base é obrigatória';
          }
        }
        break;
      case 3:
        if (wizardMode === 'INHERIT') {
          const template = templates.find(t => t.id === wizardFormData.templateId);
          const schemaArray = getCredentialSchemaArray(template?.credentialSchema);
          schemaArray.forEach(field => {
            if (field.required && !wizardFormData.credentials[field.name]?.trim()) {
              errors[`cred_${field.name}`] = `${field.label} é obrigatório`;
            }
          });
        } else {
          if (!wizardFormData.credentials.apiKey?.trim()) {
            errors.cred_apiKey = 'API Key é obrigatória';
          }
        }
        break;
      case 4:
        // No validation needed for optional fields
        break;
    }
    
    setWizardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWizardNext = () => {
    if (validateWizardStep(wizardStep)) {
      setWizardStep((prev) => Math.min(prev + 1, 5) as WizardStep);
    }
  };

  const handleWizardPrev = () => {
    setWizardStep((prev) => Math.max(prev - 1, 1) as WizardStep);
  };

  const handleSelectTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    setWizardFormData(prev => ({
      ...prev,
      templateId,
      category: template?.category || 'BAAS',
      type: template?.type || 'REST',
      baseUrl: template?.baseUrl || '',
      authType: template?.authType || 'API_KEY',
      credentials: {},
    }));
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setConnectionTest({ status: 'TESTING' });
    
    try {
      const payload = {
        templateId: wizardMode === 'INHERIT' ? wizardFormData.templateId : undefined,
        credentials: wizardFormData.credentials,
        baseUrl: wizardMode === 'SCRATCH' ? wizardFormData.baseUrl : undefined,
      };
      
      const response = await api.post('/provider-instances/test-connection', payload);
      
      setConnectionTest({
        status: response.data.success ? 'SUCCESS' : 'FAILED',
        message: response.data.message,
        latency: response.data.latency,
      });
    } catch (error: any) {
      setConnectionTest({
        status: 'FAILED',
        message: error.response?.data?.message || 'Falha ao testar conexão',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleCreateInstance = async () => {
    if (!validateWizardStep(5)) return;
    
    setIsCreating(true);
    try {
      const payload = {
        name: wizardFormData.name,
        accountId: wizardFormData.accountId,
        templateId: wizardFormData.templateId,
        credentials: wizardFormData.credentials,
        priority: wizardFormData.priority,
      };
      
      await api.post('/provider-instances', payload);
      handleCloseCreateWizard();
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao criar instância');
    } finally {
      setIsCreating(false);
    }
  };

  // ============================================
  // CRUD HANDLERS
  // ============================================

  const handleViewDetails = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setShowDetailsModal(true);
  };

  const handleOpenEdit = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setEditFormData({
      name: instance.name,
      priority: instance.priority,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedInstance) return;
    setIsEditing(true);
    try {
      await api.put(`/provider-instances/${selectedInstance.id}`, editFormData);
      setShowEditModal(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar instância');
    } finally {
      setIsEditing(false);
    }
  };

  const handleOpenClone = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setCloneName(`${instance.name} (Cópia)`);
    setCloneTenantId(instance.tenantId);
    setShowCloneModal(true);
  };

  const handleConfirmClone = async () => {
    if (!selectedInstance) return;
    setIsCloning(true);
    try {
      await api.post(`/provider-instances/${selectedInstance.id}/clone`, {
        name: cloneName,
        tenantId: cloneTenantId || undefined,
      });
      setShowCloneModal(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao clonar instância');
    } finally {
      setIsCloning(false);
    }
  };

  const handleOpenDelete = (instance: ProviderInstance) => {
    setSelectedInstance(instance);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedInstance) return;
    setIsDeleting(true);
    try {
      await api.delete(`/provider-instances/${selectedInstance.id}`);
      setShowDeleteConfirm(false);
      setShowDetailsModal(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir instância');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTestInstanceConnection = async (instance: ProviderInstance) => {
    try {
      const response = await api.post(`/provider-instances/${instance.id}/test-connection`);
      alert(response.data.success ? `✅ Conexão OK (${response.data.latency}ms)` : `❌ Falha: ${response.data.message}`);
      fetchData();
    } catch (error: any) {
      alert(`❌ Erro: ${error.response?.data?.message || 'Falha ao testar conexão'}`);
    }
  };

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const totalInstances = instances.length;
  const activeInstances = instances.filter(i => i.status === 'ACTIVE').length;
  const errorInstances = instances.filter(i => i.status === 'ERROR').length;
  const totalRequests = instances.reduce((sum, i) => sum + (i.totalRequests || i.requestCount || 0), 0);

  const tenantOptions = [
    { value: '', label: 'Todos os Tenants' },
    ...tenants.map(t => ({ value: t.id, label: t.name }))
  ];

  const templateOptions = [
    { value: '', label: 'Todos os Templates' },
    ...templates.map(t => ({ value: t.id, label: `${t.name} (${t.category})` }))
  ];

  const accountOptions = accounts.map(a => ({ 
    value: a.id, 
    label: `${a.name} (${a.type})${a.tenant ? ` - ${a.tenant.name}` : ''}` 
  }));

  const selectedTemplate = templates.find(t => t.id === wizardFormData.templateId);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const getCategoryInfo = (category: string) => CATEGORY_INFO[category] || CATEGORY_INFO.BAAS;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'INACTIVE':
        return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" />Inativo</Badge>;
      case 'ERROR':
        return <Badge variant="error"><XCircle className="w-3 h-3 mr-1" />Erro</Badge>;
      case 'PENDING':
        return <Badge variant="info"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'TESTING':
        return <Badge variant="info"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Testando</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const maskCredential = (value: string) => {
    if (!value) return '***';
    if (value.length <= 8) return '****';
    return value.substring(0, 4) + '****' + value.substring(value.length - 4);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // ============================================
  // RENDER - Wizard Steps
  // ============================================

  const renderWizardStep = () => {
    switch (wizardStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-surface-100 mb-2">Como deseja criar a instância?</h3>
              <p className="text-surface-400">Escolha entre herdar configurações de um template existente ou criar do zero.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Inherit from Template */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setWizardMode('INHERIT')}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  wizardMode === 'INHERIT'
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    wizardMode === 'INHERIT' ? 'bg-primary-500/20' : 'bg-surface-700'
                  }`}>
                    <Puzzle className={`w-6 h-6 ${wizardMode === 'INHERIT' ? 'text-primary-400' : 'text-surface-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-surface-100">Herdar de Template</h4>
                    <p className="text-sm text-surface-400">Recomendado</p>
                  </div>
                  {wizardMode === 'INHERIT' && (
                    <CheckCircle className="w-6 h-6 text-primary-400 ml-auto" />
                  )}
                </div>
                <p className="text-sm text-surface-400">
                  Use um template pré-configurado com mapeamentos de operações, autenticação e webhooks já definidos.
                  Ideal para providers conhecidos como OpenPix, Asaas, iugu.
                </p>
              </motion.div>
              
              {/* Create from Scratch */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setWizardMode('SCRATCH')}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  wizardMode === 'SCRATCH'
                    ? 'border-accent-500 bg-accent-500/10'
                    : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    wizardMode === 'SCRATCH' ? 'bg-accent-500/20' : 'bg-surface-700'
                  }`}>
                    <FileCode className={`w-6 h-6 ${wizardMode === 'SCRATCH' ? 'text-accent-400' : 'text-surface-400'}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-surface-100">Criar do Zero</h4>
                    <p className="text-sm text-surface-400">Avançado</p>
                  </div>
                  {wizardMode === 'SCRATCH' && (
                    <CheckCircle className="w-6 h-6 text-accent-400 ml-auto" />
                  )}
                </div>
                <p className="text-sm text-surface-400">
                  Configure manualmente todos os parâmetros da instância.
                  Use para providers customizados ou integrações específicas.
                </p>
              </motion.div>
            </div>
            
            {wizardErrors.mode && (
              <p className="text-red-400 text-sm text-center">{wizardErrors.mode}</p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome da Instância *"
                value={wizardFormData.name}
                onChange={(e) => setWizardFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: OpenPix Produção"
                error={wizardErrors.name}
              />
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">Conta (Escrow) *</label>
                <Select
                  value={wizardFormData.accountId}
                  onChange={(e) => setWizardFormData(prev => ({ ...prev, accountId: e.target.value }))}
                  options={accountOptions.length > 0 ? accountOptions : [{ value: '', label: 'Nenhuma conta disponível' }]}
                  className="w-full"
                />
                {wizardErrors.accountId && <p className="text-red-400 text-xs mt-1">{wizardErrors.accountId}</p>}
              </div>
            </div>
            
            {wizardMode === 'INHERIT' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">Selecione um Template *</label>
                  {wizardErrors.templateId && <p className="text-red-400 text-xs mb-2">{wizardErrors.templateId}</p>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-2">
                    {templates.filter(t => t.status === 'ACTIVE' || t.status === 'PUBLISHED').map(template => {
                      const catInfo = getCategoryInfo(template.category);
                      return (
                        <motion.div
                          key={template.id}
                          whileHover={{ scale: 1.01 }}
                          onClick={() => handleSelectTemplate(template.id)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            wizardFormData.templateId === template.id
                              ? 'border-primary-500 bg-primary-500/10'
                              : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center`}>
                              <span className="text-lg">{catInfo.emoji}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-surface-100 truncate">{template.name}</p>
                              <p className="text-xs text-surface-400">{template.category} • {template.type} • v{template.version}</p>
                            </div>
                            {wizardFormData.templateId === template.id && (
                              <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                            )}
                          </div>
                          {template.description && (
                            <p className="text-xs text-surface-500 mt-2 line-clamp-2">{template.description}</p>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
                
                {selectedTemplate && (
                  <Card className="p-4 bg-surface-800/50 border-surface-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4 text-primary-400" />
                      <span className="text-sm font-medium text-surface-200">Template Selecionado</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-surface-500">Categoria</p>
                        <p className="text-surface-200">{selectedTemplate.category}</p>
                      </div>
                      <div>
                        <p className="text-surface-500">Tipo</p>
                        <p className="text-surface-200">{selectedTemplate.type}</p>
                      </div>
                      <div>
                        <p className="text-surface-500">Autenticação</p>
                        <p className="text-surface-200">{selectedTemplate.authType || 'API_KEY'}</p>
                      </div>
                      <div>
                        <p className="text-surface-500">Versão</p>
                        <p className="text-surface-200">v{selectedTemplate.version}</p>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">Categoria *</label>
                    <Select
                      value={wizardFormData.category}
                      onChange={(e) => setWizardFormData(prev => ({ ...prev, category: e.target.value }))}
                      options={[
                        { value: 'BAAS', label: '🏦 BaaS (Banking)' },
                        { value: 'CRYPTO', label: '₿ Crypto' },
                        { value: 'CARDS', label: '💳 Cartões' },
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">Tipo *</label>
                    <Select
                      value={wizardFormData.type}
                      onChange={(e) => setWizardFormData(prev => ({ ...prev, type: e.target.value }))}
                      options={[
                        { value: 'REST', label: 'REST API' },
                        { value: 'GRAPHQL', label: 'GraphQL' },
                        { value: 'EVM', label: 'EVM (Ethereum)' },
                        { value: 'SOLANA', label: 'Solana' },
                        { value: 'DEX', label: 'DEX' },
                      ]}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <Input
                  label="URL Base *"
                  value={wizardFormData.baseUrl}
                  onChange={(e) => setWizardFormData(prev => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="https://api.provider.com/v1"
                  error={wizardErrors.baseUrl}
                  leftIcon={<Globe className="w-4 h-4" />}
                />
                
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">Tipo de Autenticação *</label>
                  <Select
                    value={wizardFormData.authType}
                    onChange={(e) => setWizardFormData(prev => ({ ...prev, authType: e.target.value }))}
                    options={AUTH_TYPE_OPTIONS}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>
        );

      case 3:
        const credentialFields = selectedTemplate ? getCredentialSchemaArray(selectedTemplate.credentialSchema) : [];
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-surface-100 mb-2">Credenciais de Autenticação</h3>
              <p className="text-surface-400">
                {wizardMode === 'INHERIT' && selectedTemplate
                  ? `Configure as credenciais para ${selectedTemplate.name}`
                  : 'Configure as credenciais de acesso ao provider'}
              </p>
            </div>
            
            {wizardMode === 'INHERIT' && credentialFields.length > 0 ? (
              <div className="space-y-4">
                {credentialFields.map(field => (
                  <div key={field.name}>
                    <Input
                      label={`${field.label} ${field.required ? '*' : ''}`}
                      type={field.type === 'password' ? 'password' : 'text'}
                      value={wizardFormData.credentials[field.name] || ''}
                      onChange={(e) => setWizardFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, [field.name]: e.target.value }
                      }))}
                      placeholder={field.placeholder}
                      error={wizardErrors[`cred_${field.name}`]}
                    />
                    {field.description && (
                      <p className="text-xs text-surface-500 mt-1">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <Input
                  label="API Key *"
                  type="password"
                  value={wizardFormData.credentials.apiKey || ''}
                  onChange={(e) => setWizardFormData(prev => ({
                    ...prev,
                    credentials: { ...prev.credentials, apiKey: e.target.value }
                  }))}
                  placeholder="Sua API Key"
                  error={wizardErrors.cred_apiKey}
                  leftIcon={<Key className="w-4 h-4" />}
                />
                
                {wizardFormData.authType === 'BASIC' && (
                  <>
                    <Input
                      label="Username"
                      value={wizardFormData.credentials.username || ''}
                      onChange={(e) => setWizardFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, username: e.target.value }
                      }))}
                      placeholder="Username"
                    />
                    <Input
                      label="Password"
                      type="password"
                      value={wizardFormData.credentials.password || ''}
                      onChange={(e) => setWizardFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, password: e.target.value }
                      }))}
                      placeholder="Password"
                    />
                  </>
                )}
                
                {wizardFormData.authType === 'OAUTH2' && (
                  <>
                    <Input
                      label="Client ID"
                      value={wizardFormData.credentials.clientId || ''}
                      onChange={(e) => setWizardFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, clientId: e.target.value }
                      }))}
                      placeholder="Client ID"
                    />
                    <Input
                      label="Client Secret"
                      type="password"
                      value={wizardFormData.credentials.clientSecret || ''}
                      onChange={(e) => setWizardFormData(prev => ({
                        ...prev,
                        credentials: { ...prev.credentials, clientSecret: e.target.value }
                      }))}
                      placeholder="Client Secret"
                    />
                  </>
                )}
              </div>
            )}
            
            <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-300">Segurança</h4>
                  <p className="text-sm text-surface-400 mt-1">
                    As credenciais são criptografadas com AES-256 antes de serem armazenadas.
                    Nunca compartilhe suas credenciais de produção.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );


      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-surface-100 mb-2">Configurações Avançadas</h3>
              <p className="text-surface-400">Configurações opcionais para personalizar a instância.</p>
            </div>
            
            <Input
              label="Prioridade"
              type="number"
              value={wizardFormData.priority.toString()}
              onChange={(e) => setWizardFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
              placeholder="0"
            />
            <p className="text-xs text-surface-500 -mt-4">
              Instâncias com menor prioridade são usadas primeiro em caso de fallback (0 = maior prioridade).
            </p>
            
            <Card className="p-4 bg-blue-500/10 border-blue-500/30">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-300">Dica</h4>
                  <p className="text-sm text-surface-400 mt-1">
                    A prioridade define a ordem de uso quando múltiplas instâncias estão disponíveis.
                    Use valores menores para instâncias principais e maiores para fallback.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-surface-100 mb-2">Revisão e Teste</h3>
              <p className="text-surface-400">Revise as configurações e teste a conexão antes de criar.</p>
            </div>
            
            {/* Summary */}
            <Card className="p-4 bg-surface-800/50">
              <h4 className="font-medium text-surface-200 mb-4">Resumo da Configuração</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-surface-500">Nome</p>
                  <p className="text-surface-100 font-medium">{wizardFormData.name}</p>
                </div>
                <div>
                  <p className="text-surface-500">Conta</p>
                  <p className="text-surface-100 font-medium">
                    {accounts.find(a => a.id === wizardFormData.accountId)?.name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-surface-500">Modo</p>
                  <p className="text-surface-100 font-medium">
                    {wizardMode === 'INHERIT' ? 'Herdar de Template' : 'Criar do Zero'}
                  </p>
                </div>
                {wizardMode === 'INHERIT' && selectedTemplate && (
                  <div>
                    <p className="text-surface-500">Template</p>
                    <p className="text-surface-100 font-medium">{selectedTemplate.name}</p>
                  </div>
                )}
                <div>
                  <p className="text-surface-500">Categoria</p>
                  <p className="text-surface-100 font-medium">
                    {wizardMode === 'INHERIT' ? selectedTemplate?.category : wizardFormData.category}
                  </p>
                </div>
                <div>
                  <p className="text-surface-500">Prioridade</p>
                  <p className="text-surface-100 font-medium">{wizardFormData.priority}</p>
                </div>
              </div>
            </Card>
            
            {/* Credentials Summary */}
            <Card className="p-4 bg-surface-800/50">
              <h4 className="font-medium text-surface-200 mb-3">Credenciais Configuradas</h4>
              <div className="flex flex-wrap gap-2">
                {Object.keys(wizardFormData.credentials).map(key => (
                  <Badge key={key} variant="info">
                    <Key className="w-3 h-3 mr-1" />
                    {key}: ****
                  </Badge>
                ))}
              </div>
            </Card>
            
            {/* Connection Test */}
            <Card className={`p-4 ${
              connectionTest.status === 'SUCCESS' ? 'bg-green-500/10 border-green-500/30' :
              connectionTest.status === 'FAILED' ? 'bg-red-500/10 border-red-500/30' :
              'bg-surface-800/50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {connectionTest.status === 'PENDING' && (
                    <Activity className="w-5 h-5 text-surface-400" />
                  )}
                  {connectionTest.status === 'TESTING' && (
                    <RefreshCw className="w-5 h-5 text-primary-400 animate-spin" />
                  )}
                  {connectionTest.status === 'SUCCESS' && (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                  {connectionTest.status === 'FAILED' && (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <div>
                    <h4 className="font-medium text-surface-200">Teste de Conexão</h4>
                    <p className="text-sm text-surface-400">
                      {connectionTest.status === 'PENDING' && 'Clique para testar a conexão'}
                      {connectionTest.status === 'TESTING' && 'Testando conexão...'}
                      {connectionTest.status === 'SUCCESS' && `Conexão OK${connectionTest.latency ? ` (${connectionTest.latency}ms)` : ''}`}
                      {connectionTest.status === 'FAILED' && (connectionTest.message || 'Falha na conexão')}
                    </p>
                  </div>
                </div>
                <Button
                  variant={connectionTest.status === 'SUCCESS' ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={handleTestConnection}
                  disabled={isTesting}
                >
                  {isTesting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      {connectionTest.status === 'SUCCESS' ? 'Testar Novamente' : 'Testar Conexão'}
                    </>
                  )}
                </Button>
              </div>
            </Card>
            
            {connectionTest.status === 'FAILED' && (
              <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-300">Atenção</h4>
                    <p className="text-sm text-surface-400 mt-1">
                      O teste de conexão falhou, mas você ainda pode criar a instância.
                      Verifique as credenciais e tente novamente após a criação.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center shadow-lg shadow-accent-500/25">
              <Server className="w-8 h-8 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-surface-900 border-2 border-accent-500 flex items-center justify-center">
              <Activity className="w-3 h-3 text-accent-400" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-2">
              Provider Instances
              <Badge variant="primary" size="sm">{totalInstances}</Badge>
            </h1>
            <p className="text-surface-400">
              Conexões ativas de providers por tenant
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={fetchData}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={handleOpenCreateWizard}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Instância
          </Button>
        </div>
      </div>

      {/* ============================================ */}
      {/* STATS CARDS */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-primary-500/10 to-transparent border-primary-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Server className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{totalInstances}</p>
              <p className="text-sm text-surface-400">Total Instâncias</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{activeInstances}</p>
              <p className="text-sm text-surface-400">Ativas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{errorInstances}</p>
              <p className="text-sm text-surface-400">Com Erro</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{totalRequests.toLocaleString()}</p>
              <p className="text-sm text-surface-400">Total Requests</p>
            </div>
          </div>
        </Card>
      </div>

      {/* ============================================ */}
      {/* FILTERS */}
      {/* ============================================ */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-surface-400" />
          <span className="text-sm font-medium text-surface-300">Filtros</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-[300px]">
            <Input
              placeholder="Buscar instâncias..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-surface-500" />
            <Select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              options={tenantOptions}
              className="w-auto min-w-[140px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Puzzle className="w-4 h-4 text-surface-500" />
            <Select
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              options={templateOptions}
              className="w-auto min-w-[160px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-surface-500" />
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              options={CATEGORY_OPTIONS}
              className="w-auto min-w-[150px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-surface-500" />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={STATUS_OPTIONS}
              className="w-auto min-w-[130px]"
            />
          </div>
        </div>
      </Card>

      {/* ============================================ */}
      {/* INSTANCES TABLE */}
      {/* ============================================ */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : instances.length === 0 ? (
        <Card className="p-12 text-center">
          <Server className="w-16 h-16 text-surface-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-surface-300 mb-2">Nenhuma instância encontrada</h3>
          <p className="text-surface-400 mb-4">
            {tenantFilter || templateFilter || categoryFilter || statusFilter
              ? 'Tente ajustar os filtros'
              : 'Crie a primeira instância de provider'}
          </p>
          <Button onClick={handleOpenCreateWizard}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Instância
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Instância</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Tenant</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Template</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Requests</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Último Uso</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              <AnimatePresence>
                {instances.map((instance, index) => {
                  const categoryInfo = getCategoryInfo(instance.template?.category || 'BAAS');
                  return (
                    <motion.tr
                      key={instance.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="hover:bg-surface-800/50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${categoryInfo.gradient} flex items-center justify-center`}>
                            <span className="text-lg">{categoryInfo.emoji}</span>
                          </div>
                          <div>
                            <p className="font-medium text-surface-100">{instance.name}</p>
                            <p className="text-xs text-surface-500">ID: {instance.id.substring(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-surface-500" />
                          <span className="text-surface-300">{instance.tenant?.name || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Puzzle className="w-4 h-4 text-surface-500" />
                          <div>
                            <p className="text-surface-300">{instance.template?.name || 'N/A'}</p>
                            <p className="text-xs text-surface-500">v{instance.template?.version || '?'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(instance.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-surface-500" />
                          <span className="text-surface-300">{(instance.totalRequests || instance.requestCount || 0).toLocaleString()}</span>
                          {(instance.errorCount || 0) > 0 && (
                            <Badge variant="error" size="sm">{instance.errorCount} erros</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-surface-400 text-sm">
                          {instance.lastUsedAt ? formatDate(instance.lastUsedAt) : 'Nunca'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetails(instance)} title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(instance)} title="Editar">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleTestInstanceConnection(instance)} title="Testar conexão">
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenClone(instance)} title="Clonar">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(instance)} title="Excluir">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </Card>
      )}


      {/* ============================================ */}
      {/* CREATE WIZARD MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showCreateWizard}
        onClose={handleCloseCreateWizard}
        title="Nova Instância de Provider"
        size="2xl"
      >
        <div className="space-y-6">
          {/* Wizard Steps Indicator */}
          <div className="flex items-center justify-between px-4">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    wizardStep === step.step
                      ? 'bg-primary-500 text-white'
                      : wizardStep > step.step
                        ? 'bg-green-500 text-white'
                        : 'bg-surface-700 text-surface-400'
                  }`}>
                    {wizardStep > step.step ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      step.step
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p className={`text-xs font-medium ${
                      wizardStep >= step.step ? 'text-surface-200' : 'text-surface-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-surface-500 hidden md:block">{step.description}</p>
                  </div>
                </div>
                {index < WIZARD_STEPS.length - 1 && (
                  <div className={`w-12 md:w-20 h-0.5 mx-2 ${
                    wizardStep > step.step ? 'bg-green-500' : 'bg-surface-700'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">
            {renderWizardStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-surface-700">
            <Button
              variant="ghost"
              onClick={wizardStep === 1 ? handleCloseCreateWizard : handleWizardPrev}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {wizardStep === 1 ? 'Cancelar' : 'Voltar'}
            </Button>
            
            {wizardStep < 5 ? (
              <Button onClick={handleWizardNext}>
                Próximo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleCreateInstance} disabled={isCreating}>
                {isCreating ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Criar Instância
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* DETAILS MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Detalhes: ${selectedInstance?.name || ''}`}
        size="xl"
      >
        {selectedInstance && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getCategoryInfo(selectedInstance.template?.category || 'BAAS').gradient} flex items-center justify-center`}>
                <span className="text-2xl">{getCategoryInfo(selectedInstance.template?.category || 'BAAS').emoji}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-semibold text-surface-100">
                    {selectedInstance.name}
                  </h3>
                  {getStatusBadge(selectedInstance.status)}
                </div>
                <p className="text-surface-400">
                  Template: {selectedInstance.template?.name} v{selectedInstance.template?.version}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-surface-500">ID: {selectedInstance.id}</span>
                  <button onClick={() => copyToClipboard(selectedInstance.id)} className="p-1 hover:bg-surface-700 rounded">
                    <Copy className="w-3 h-3 text-surface-400" />
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setShowDetailsModal(false); handleOpenEdit(selectedInstance); }}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleTestInstanceConnection(selectedInstance)}>
                  <Play className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-3 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-surface-500" />
                  <p className="text-xs text-surface-500">Tenant</p>
                </div>
                <p className="text-surface-100 font-medium">{selectedInstance.tenant?.name || 'N/A'}</p>
              </Card>
              <Card className="p-3 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-surface-500" />
                  <p className="text-xs text-surface-500">Requests</p>
                </div>
                <p className="text-surface-100 font-medium">{(selectedInstance.totalRequests || selectedInstance.requestCount || 0).toLocaleString()}</p>
              </Card>
              <Card className="p-3 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-surface-500" />
                  <p className="text-xs text-surface-500">Erros</p>
                </div>
                <p className={`font-medium ${(selectedInstance.errorCount || 0) > 0 ? 'text-red-400' : 'text-surface-100'}`}>
                  {selectedInstance.errorCount || 0}
                </p>
              </Card>
              <Card className="p-3 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-surface-500" />
                  <p className="text-xs text-surface-500">Tempo Médio</p>
                </div>
                <p className="text-surface-100 font-medium">
                  {selectedInstance.avgResponseTime ? `${selectedInstance.avgResponseTime}ms` : 'N/A'}
                </p>
              </Card>
            </div>

            {/* Credentials (Masked) */}
            <Card className="p-4 bg-surface-800/50">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-4 h-4 text-yellow-400" />
                <h4 className="font-medium text-surface-100">Credenciais (Mascaradas)</h4>
              </div>
              {selectedInstance.credentials && Object.keys(selectedInstance.credentials).length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(selectedInstance.credentials).map(([key, value]) => (
                    <div key={key} className="p-2 bg-surface-900 rounded">
                      <p className="text-xs text-surface-500 mb-1">{key}</p>
                      <p className="text-surface-300 font-mono text-sm">{maskCredential(value)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-surface-500 text-sm">Nenhuma credencial configurada</p>
              )}
            </Card>

            {/* Webhook URL */}
            {selectedInstance.webhookUrl && (
              <Card className="p-4 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-2">
                  <Link className="w-4 h-4 text-blue-400" />
                  <h4 className="font-medium text-surface-100">Webhook URL</h4>
                </div>
                <p className="text-surface-300 font-mono text-sm break-all">{selectedInstance.webhookUrl}</p>
              </Card>
            )}

            {/* Config Overrides */}
            {selectedInstance.configOverrides && Object.keys(selectedInstance.configOverrides).length > 0 && (
              <Card className="p-4 bg-surface-800/50">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-4 h-4 text-blue-400" />
                  <h4 className="font-medium text-surface-100">Configurações Personalizadas</h4>
                </div>
                <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                  {JSON.stringify(selectedInstance.configOverrides, null, 2)}
                </pre>
              </Card>
            )}

            {/* Timestamps */}
            <div className="flex items-center justify-between text-sm text-surface-500 pt-4 border-t border-surface-700">
              <span>Criado em: {formatDate(selectedInstance.createdAt)}</span>
              <span>Último uso: {selectedInstance.lastUsedAt ? formatDate(selectedInstance.lastUsedAt) : 'Nunca'}</span>
            </div>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* EDIT MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Editar: ${selectedInstance?.name || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={editFormData.name || ''}
            onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
          />
          
          <Input
            label="Prioridade"
            type="number"
            value={editFormData.priority?.toString() || '0'}
            onChange={(e) => setEditFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
          />
          <p className="text-xs text-surface-500 -mt-2">
            Menor valor = maior prioridade no roteamento
          </p>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isEditing}>
              {isEditing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* CLONE MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        title="Clonar Instância"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-surface-400">
            Criar uma cópia de <span className="text-surface-200 font-medium">{selectedInstance?.name}</span>
          </p>
          
          <Input
            label="Nome da Nova Instância"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            placeholder="Nome da cópia"
          />
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Tenant (opcional)</label>
            <Select
              value={cloneTenantId}
              onChange={(e) => setCloneTenantId(e.target.value)}
              options={[
                { value: '', label: 'Mesmo tenant' },
                ...tenants.map(t => ({ value: t.id, label: t.name }))
              ]}
              className="w-full"
            />
            <p className="text-xs text-surface-500 mt-1">Deixe vazio para clonar no mesmo tenant</p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setShowCloneModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmClone} disabled={isCloning || !cloneName.trim()}>
              {isCloning ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              Clonar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Confirmar Exclusão"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-300">Atenção!</h4>
              <p className="text-sm text-surface-400 mt-1">
                Você está prestes a excluir a instância <span className="text-surface-200 font-medium">{selectedInstance?.name}</span>.
                Esta ação não pode ser desfeita.
              </p>
            </div>
          </div>
          
          {selectedInstance && (selectedInstance.totalRequests || selectedInstance.requestCount || 0) > 0 && (
            <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-300">Instância em uso</h4>
                  <p className="text-sm text-surface-400 mt-1">
                    Esta instância possui {(selectedInstance.totalRequests || selectedInstance.requestCount || 0).toLocaleString()} requisições registradas.
                    Certifique-se de que não há dependências ativas antes de excluir.
                  </p>
                </div>
              </div>
            </Card>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Excluir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
